// ============================================================
// TargetGearWeapons - the Target Gear tab's weapons card
// ============================================================
// The defender's weapons in parry order: the first valid melee weapon is
// starred as the one it parries with, and up/down arrows reorder them.

import type { CSSProperties } from 'react';
import type { DefenderWeapon } from '../types';
import { fmtWS } from '../engine/weaponSpecials';
import { findParryWeaponIndex } from '../engine/parryWeapon';
import { swapped } from './targetGear';
import common from '../styles/common.module.css';
import styles from '../styles/TargetPanel.module.css';

/** Weapon specials that affect defence - highlighted with accent colors */
const DEFENCE_SPECIALS = new Set(['Balanced', 'Unbalanced', 'Unwieldy', 'PowerField', 'Defensive']);

const EMPTY_NOTE: CSSProperties = { fontSize: 10, color: 'var(--text-faint)', textAlign: 'center', padding: 4 };

interface Props {
  weapons: DefenderWeapon[];
  onReorderWeapons?: (weapons: DefenderWeapon[]) => void;
  locked: boolean;
}

export function TargetGearWeapons(props: Props) {
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>WEAPONS</div>
      {props.weapons.length === 0 ? <div style={EMPTY_NOTE}>No weapons.</div> : <WeaponList {...props} />}
    </div>
  );
}

function WeaponList({ weapons, onReorderWeapons, locked }: Props) {
  const parryIdx = findParryWeaponIndex(weapons);
  const swap = (from: number, to: number) => {
    if (locked || !onReorderWeapons) return;
    onReorderWeapons(swapped(weapons, from, to));
  };
  const reorderable = !locked && weapons.length > 1;
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {weapons.map((w, i) => (
          <WeaponRow
            key={w.id}
            weapon={w}
            isParryWeapon={i === parryIdx}
            arrows={reorderable ? { first: i === 0, last: i === weapons.length - 1 } : null}
            onMove={(step) => swap(i, i + step)}
          />
        ))}
      </div>
      <div style={{ fontSize: 9, color: 'var(--text-faint)', fontStyle: 'italic', marginTop: 4, padding: '0 6px' }}>
        First valid melee weapon is used for parrying.
      </div>
    </>
  );
}

interface WeaponRowProps {
  weapon: DefenderWeapon;
  isParryWeapon: boolean;
  /** Where the row sits, when it can be moved at all. */
  arrows: { first: boolean; last: boolean } | null;
  /** Move by -1 (up) or +1 (down). */
  onMove: (step: number) => void;
}

function WeaponRow({ weapon: w, isParryWeapon, arrows, onMove }: WeaponRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '3px 6px',
        background: isParryWeapon ? 'var(--bg-card)' : 'var(--bg-elevated)',
        borderRadius: 'var(--radius-sm)',
        border: isParryWeapon ? '1px solid var(--accent)' : '1px solid transparent',
        fontSize: 11,
        gap: 4,
      }}
    >
      {arrows && <ReorderArrows name={w.name} {...arrows} onMove={onMove} />}
      {isParryWeapon && (
        <span
          title="Parry weapon"
          style={{ color: 'var(--accent)', fontSize: 12, lineHeight: 1 }}
          data-testid="parry-indicator"
        >
          &#9733;
        </span>
      )}
      <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', flex: 1 }}>{w.name}</span>
      <span style={{ fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase' }}>{w.type}</span>
      {w.specialRules.length > 0 && (
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {w.specialRules.map((sr) => (
            <SpecialBadge key={sr} rule={sr} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ReorderArrowsProps {
  name: string;
  first: boolean;
  last: boolean;
  onMove: (step: number) => void;
}

/** Up and down; the one with nowhere to go is hidden, not removed, so the rows stay aligned. */
function ReorderArrows({ name, first, last, onMove }: ReorderArrowsProps) {
  const arrowStyle = (hidden: boolean): CSSProperties => ({
    fontSize: 8,
    padding: '0 3px',
    lineHeight: 1,
    visibility: hidden ? 'hidden' : 'visible',
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginRight: 2 }}>
      <button
        className={common.btnSmall}
        style={arrowStyle(first)}
        onClick={() => onMove(-1)}
        title="Move up"
        aria-label={`Move ${name} up`}
      >
        &#9650;
      </button>
      <button
        className={common.btnSmall}
        style={arrowStyle(last)}
        onClick={() => onMove(1)}
        title="Move down"
        aria-label={`Move ${name} down`}
      >
        &#9660;
      </button>
    </div>
  );
}

/** A special rule's badge, accented when it bears on defence. */
function SpecialBadge({ rule }: { rule: string }) {
  const isDefence = DEFENCE_SPECIALS.has(rule.replace(/\(\d+\)$/, ''));
  return (
    <span
      style={{
        fontSize: 8,
        padding: '1px 4px',
        borderRadius: 3,
        background: isDefence ? 'var(--accent-dim)' : 'var(--bg-input)',
        color: isDefence ? 'var(--accent)' : 'var(--text-dim)',
        border: isDefence ? '1px solid var(--accent)' : '1px solid var(--border-subtle)',
      }}
    >
      {fmtWS(rule)}
    </span>
  );
}
